import { useMemo, useState } from 'react';
import cm from 'classnames';

import { AspectRatioStory, StoryConfig } from 'services/story.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { setTitleCredits } from 'components/app/editor/_mediator/editor-controls.slice';
import { useEditorTitleCredits } from 'components/app/editor/_mediator/editor-config/use-editor-title-credits';
import { __BORDER_WIDTH, __FONT_SIZE, __POSITION } from './_title-credits.params';

import { Select } from 'components/UI/select/select';
import { Button } from 'components/UI/button/button';
import { InputBase } from 'components/UI/input/elements/input-base';
import { InputArea } from 'components/UI/input/elements/input-area';
import { InputCheck } from 'components/UI/input/elements/input-check';
import { __Slider } from './_slider';
import { __Section } from './_section';
import { __Preview } from './_preview';
import { __TextLayer } from './_text-layer';
import styles from './_title-credits.module.scss';

interface Props {
  isTitle: boolean;
  config: StoryConfig;
  onClose: () => void;
}

export const __TitleCreditsModal = ({ isTitle, config, onClose }: Props) => {
  const fonts = useAppSelector((state) => state.app__data.fonts);
  const submitData = useEditorTitleCredits();
  const dispatch = useAppDispatch();

  const [sync, setSync] = useState(false);
  const [preview, setPreview] = useState(false);

  const [text, setText] = useState(isTitle ? config.titleText : config.creditsText);
  const [preset, setPreset] = useState(isTitle ? config.titleTextPreset : config.creditsTextPreset);

  const onSubmit = async () => {
    submitData(preset, text, config.aspectRatio, isTitle, sync);
    dispatch(setTitleCredits(null));
  };

  const fontOptions = useMemo(() => {
    return fonts?.map((font) => ({ label: font.name, value: font.value }));
  }, [fonts]);

  return (
    <>
      <header className={styles.TitleCredits__header}>{isTitle ? 'Title' : 'Credits'} Text Preset</header>
      <div className={styles.TitleCredits__body}>
        <div className={styles.TitleCredits__left}>
          <__Section title="Font Family">
            <Select
              options={fontOptions}
              value={preset.font ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setPreset((prev) => ({ ...prev, font: parseInt(value) }));
              }}
            />
          </__Section>
          <__Section title="Fill Color" halfSize>
            <InputBase
              type="color"
              value={preset.fillColor}
              onChange={(e) => setPreset((prev) => ({ ...prev, fillColor: e.target.value }))}
            />
          </__Section>
          <__Section title="Shadow Color" halfSize>
            <InputBase
              type="color"
              value={preset.shadowColor}
              onChange={(e) => setPreset((prev) => ({ ...prev, shadowColor: e.target.value }))}
            />
          </__Section>
          <__Section title="Border Color" halfSize>
            <InputBase
              type="color"
              value={preset.borderColor}
              onChange={(e) => setPreset((prev) => ({ ...prev, borderColor: e.target.value }))}
            />
          </__Section>
          <__Section title="Border Width">
            <__Slider
              value={preset.borderWidth}
              onChange={(e) => setPreset((prev) => ({ ...prev, borderWidth: +e.target.value }))}
              params={__BORDER_WIDTH}
            />
          </__Section>
          <__Section title="Font Size">
            <__Slider
              value={preset.fontSize}
              onChange={(e) => setPreset((prev) => ({ ...prev, fontSize: +e.target.value }))}
              params={__FONT_SIZE}
            />
          </__Section>
          <__Section title="Position X">
            <__Slider
              value={preset.coordinates.x}
              onChange={(e) =>
                setPreset((prev) => ({ ...prev, coordinates: { ...prev.coordinates, x: +e.target.value } }))
              }
              params={__POSITION}
            />
          </__Section>
          <__Section title="Position Y">
            <__Slider
              value={preset.coordinates.y}
              onChange={(e) =>
                setPreset((prev) => ({ ...prev, coordinates: { ...prev.coordinates, y: +e.target.value } }))
              }
              params={__POSITION}
            />
          </__Section>
          <__Section title="Example Text">
            <InputArea value={text} onChange={(e) => setText(e.target.value)} />
          </__Section>
        </div>
        <div className={styles.TitleCredits__right}>
          <div
            className={cm(styles.TitleCredits__preview, {
              [styles['TitleCredits__preview--vertical']]: config.aspectRatio === AspectRatioStory.is9x16,
            })}
          >
            <__Preview preview={preview} isTitle={isTitle} />
            <__TextLayer text={text} preset={preset} />
          </div>
          <InputCheck value={preview} setValue={() => setPreview(!preview)} title="Enable background clip" />
        </div>
      </div>
      <footer className={styles.TitleCredits__footer}>
        <InputCheck
          title={`Synchronize to ${isTitle ? 'Credits' : 'Title'} (on Save)`}
          setValue={() => setSync(!sync)}
          value={sync}
        />
        <Button onClick={onClose} outlined>
          Cancel
        </Button>
        <Button onClick={onSubmit}>Save</Button>
      </footer>
    </>
  );
};
